import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Pagination, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { listNotifications, markAllNotificationsRead, markNotificationRead } from '../../api/notifications';
import { NotificationCategory } from '../../types/notification';
import { PRIORITY_COLOR, getNotificationMeta } from '../../config/notificationMeta';
import { useOpenNotification } from '../../hooks/useOpenNotification';
import { PageHeader } from '../../components/PageHeader';

const PAGE_SIZE = 20;

type TabValue = 'all' | 'unread' | NotificationCategory;

const TABS: { value: TabValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'leads', label: 'Leads' },
  { value: 'admissions', label: 'Admissions' },
  { value: 'documents', label: 'Documents' },
  { value: 'payments', label: 'Payments' },
];

const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

/** Notifications page (§5) — the full history the bell's "View all
 * notifications →" link opens. Tabs filter server-side (category/
 * unreadOnly query params), so pagination always reflects the true
 * filtered count, never a client-side slice of one already-fetched page. */
export const NotificationsPage = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabValue>('all');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['notifications-page', tab, page],
    queryFn: () =>
      listNotifications({
        page,
        pageSize: PAGE_SIZE,
        unreadOnly: tab === 'unread' ? true : undefined,
        category: tab === 'all' || tab === 'unread' ? undefined : tab,
      }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications-page'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
    queryClient.invalidateQueries({ queryKey: ['notification-summary'] });
  };

  const markReadMutation = useMutation({ mutationFn: markNotificationRead, onSuccess: invalidate });
  const markAllReadMutation = useMutation({ mutationFn: markAllNotificationsRead, onSuccess: invalidate });

  const openNotification = useOpenNotification();

  const notifications = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={NotificationsOutlinedIcon}
        color="#7C3AED"
        title="Notifications"
        subtitle="Your notification history across every module."
        actions={
          <Button variant="outlined" onClick={() => markAllReadMutation.mutate()}>
            Mark all read
          </Button>
        }
      />

      <Tabs
        value={tab}
        onChange={(_e, value: TabValue) => {
          setTab(value);
          setPage(1);
        }}
        sx={{ mb: 2 }}
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {query.isLoading ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Paper>
      ) : query.isError ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Couldn&rsquo;t load notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Something went wrong fetching your notifications.
          </Typography>
          <Button variant="outlined" onClick={() => query.refetch()}>
            Retry
          </Button>
        </Paper>
      ) : notifications.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            You&rsquo;re all caught up!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No notifications to show here.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {notifications.map((notification) => {
            const meta = getNotificationMeta(notification.type);
            const isUnread = !notification.readAt;
            return (
              <Paper
                key={notification.id}
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  borderLeft: '4px solid',
                  borderLeftColor: isUnread ? PRIORITY_COLOR[meta.priority] : 'divider',
                  bgcolor: isUnread ? 'action.hover' : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => void openNotification(notification)}
              >
                <Box sx={{ color: PRIORITY_COLOR[meta.priority], display: 'flex', pt: 0.25 }}>
                  <meta.icon />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: isUnread ? 700 : 500 }}>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {timeAgo(notification.createdAt)}
                  </Typography>
                </Box>
                {isUnread && (
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      markReadMutation.mutate(notification.id);
                    }}
                  >
                    Mark as read
                  </Button>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={pageCount} page={page} onChange={(_e, value) => setPage(value)} color="primary" />
        </Box>
      )}
    </Box>
  );
};
